import {Component, EventEmitter, Inject, OnInit, Output, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {EducationInfoComponent} from './education-info/education-info.component';
import {SaveEducationSettingsRequestModel} from '@core/modules/rest/user/request/save-education-settings-request.model';
import {ConsentUseCaseBasicModel} from '@core/modules/rest/consent-use-case/response/consent-use-case-basic.model';
import {YesNoDialogComponent, YesNoDialogData} from '@shared/dialogs/yes-no-dialog/yes-no-dialog.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {UserRestService} from '@core/modules/rest/user/user-rest.service';
import {NotificationService} from '@shared/components/notification/service/notification.service';

@UntilDestroy()
@Component({
    selector: 'app-choose-education-popup',
    templateUrl: './choose-education-popup.component.html',
    styleUrls: ['./choose-education-popup.component.scss']
})
export class ChooseEducationPopupComponent implements OnInit {

    @Output() closeDialogEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() saveEducationDataEvent: EventEmitter<SaveEducationSettingsRequestModel> = new EventEmitter();
    @ViewChild(EducationInfoComponent) educationInfoComponent: EducationInfoComponent;
    userId: string;
    indications = [];
    useCases = [];
    previousEducationIdValue: string;
    previousUseCaseValue: string;
    previousAllowCustomUseCaseValue: boolean;
    selectedEducationId: string;
    selectedUseCase: string;
    allowCustomUseCase: boolean;
    title: string;
    hasEducation: boolean;
    saveDisabled: boolean;

    constructor(@Inject(MAT_DIALOG_DATA) data: {
                    userId: string, indications: [], useCases: ConsentUseCaseBasicModel[],
                    selectedIndicationId: string, selectedUseCase: string, allowCustomUseCase: boolean, hasEducation: boolean, saveDisabled: boolean
                },
                private translateService: TranslateService, private dialog: MatDialog, private userService: UserRestService, private notificationService: NotificationService) {
        this.userId = data.userId;
        this.indications = data.indications;
        this.useCases = data.useCases.filter(uc => uc.uniqueTitle !== 'Custom');
        this.selectedEducationId = this.previousEducationIdValue = data.selectedIndicationId;
        this.selectedUseCase = this.previousUseCaseValue = data.selectedUseCase;
        this.allowCustomUseCase = this.previousAllowCustomUseCaseValue = data.allowCustomUseCase;
        this.hasEducation = data.hasEducation;
        this.saveDisabled = data.saveDisabled;
    }

    ngOnInit(): void {
        this.title = this.translateService.instant('USER_MANAGEMENT.CHOOSE_EDUCATION');
    }

    onSave(): void {
        this.selectedEducationId = this.educationInfoComponent.selectedEducationId;
        this.selectedUseCase = this.educationInfoComponent.selectedUseCase;
        this.allowCustomUseCase = this.educationInfoComponent.allowCustomUseCase;

        const educationPopupData: SaveEducationSettingsRequestModel = {
            userId: this.userId,
            indicationId: this.selectedEducationId,
            useCaseId: this.selectedUseCase,
            customUseCaseAllowed: this.allowCustomUseCase,
            deactivateStartedConsents: false
        };
        if (this.previousEducationIdValue !== educationPopupData.indicationId || this.previousUseCaseValue !== educationPopupData.useCaseId
            || this.previousAllowCustomUseCaseValue !== educationPopupData.customUseCaseAllowed) {
            this.checkActiveConsentConfirmation(educationPopupData);
        } else {
            this.saveEducationDataEvent.emit(educationPopupData);
        }
    }

    checkActiveConsentConfirmation(educationPopupData: SaveEducationSettingsRequestModel): void {
        this.userService.getConsentingActive(educationPopupData.userId).subscribe(async (consentingActive: boolean) => {
            if (consentingActive) {
                const confirmed = await this.openChangeEducationPopup();
                if (confirmed) {
                    educationPopupData.deactivateStartedConsents = true;
                    this.saveEducationDataEvent.emit(educationPopupData);
                }
            } else {
                this.saveEducationDataEvent.emit(educationPopupData);
            }
        });
    }

    async openChangeEducationPopup(): Promise<boolean> {
        const data: YesNoDialogData = {
            icon: 'local_library',
            iconColor: 'warning',
            title: this.translateService.instant('DIALOGS.CHANGE_EDUCATION.TITLE'),
            message: this.translateService.instant('DIALOGS.CHANGE_EDUCATION.MESSAGE'),
            yesButtonText: this.translateService.instant('GENERAL.CHANGE'),
            noButtonText: this.translateService.instant('GENERAL.CANCEL')
        };

        const dialogConfig = this.getDefaultDialogConfig();
        dialogConfig.data = data;
        const dialogRef = this.dialog.open(YesNoDialogComponent, dialogConfig);

        dialogRef.componentInstance.noEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });

        let confirmed = false;
        dialogRef.componentInstance.yesEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
            confirmed = true;
            this.notificationService.success(this.translateService.instant('NOTIFICATION.EMAIL_INVITATION_SENT'));
        });
        return dialogRef.afterClosed().toPromise().then(() => {
            return Promise.resolve(confirmed);
        });
    }

    private getDefaultDialogConfig(): MatDialogConfig {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.disableClose = false;
        return dialogConfig;
    }
}
